import type { Product } from '@/entities/product';
import { getHotelUrl, toSearchCriteriaQuery, type SearchCriteria } from '@/entities/searchCriteria';
import { getCheapestItem } from '@/shared/lib/price';
import type { QueryObject } from '@/shared/lib/serializeQuery';
import { toFiltersQuery } from './filtersUrl';
import { getPrepaymentsFilterKeys } from './prepaymentsFilter';
import { toSearchIdQuery } from './searchSession';
import { toSerpLinkSortQuery } from './serpUrl';
import type { FiltersModel, SerpProduct, SerpSort } from '../model/types';

export interface SerpHotelPageParams {
  serpProduct: SerpProduct | null;
  criteria: SearchCriteria;
  filtersModel: FiltersModel;
  sort: SerpSort;
  searchId: string;
  searchUuid: string | null;
  toReviews: boolean;
}

function getProductQuery(product: Product | null): QueryObject {
  if (!product) return {};

  return {
    identity: product.id,
    ...(product.operator ? { operatorId: product.operator.id } : {}),
    minPrice: product.price,
    ...(product.oilTax === null ? {} : { oilTaxPrice: product.oilTax }),
  };
}

function getHotelPrepayments(serpProduct: SerpProduct | null, filtersModel: FiltersModel): number[] {
  if (!serpProduct) return [];

  const available = new Set(getPrepaymentsFilterKeys([serpProduct]));

  return filtersModel.prepayments.filter((key) => available.has(key)).map(Number);
}

function getFlagsQuery(filtersModel: FiltersModel): QueryObject {
  return {
    ...(filtersModel.isExclusive ? { isExclusive: true } : {}),
    ...(filtersModel.bonusMiles ? { bonusMiles: true } : {}),
  };
}

export function getSerpHotelPageQuery({
  serpProduct,
  criteria,
  filtersModel,
  sort,
  searchId,
  searchUuid,
  toReviews,
}: SerpHotelPageParams): string {
  const prepayments = getHotelPrepayments(serpProduct, filtersModel);

  return getHotelUrl(criteria, {
    ...getProductQuery(serpProduct && getCheapestItem(serpProduct.products)),
    ...getFlagsQuery(filtersModel),
    ...(prepayments.length ? { prepayments } : {}),
    ...toSearchIdQuery(searchId),
    ...(searchUuid ? { suuid: searchUuid } : {}),
    ...(toReviews ? { toReviews: true } : {}),
    serpLink: {
      ...toSearchCriteriaQuery(criteria),
      ...toFiltersQuery(filtersModel),
      ...toSerpLinkSortQuery(sort),
    },
  });
}
